import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Avatar,
  Rating,
  Button,
  Tooltip,
  Grow,
  alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  LocationOn,
  Verified,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { trackProviderCardClick } from '../../services/analyticsService';
import FavoriteButton from '../Favorites/FavoriteButton';
import ImageGallery from '../ImageGallery/ImageGallery';
import FeaturedBadge from '../Featured/FeaturedBadge';

const AVATAR_SIZE = 46;
// Az avatar ennyit lóg a galéria alá (a CardContent felső paddingje ehhez igazodik)
const AVATAR_OVERLAP = 22;

const formatPrice = (min, max) => {
  const fmt = (n) => Number(n).toLocaleString('hu-HU');
  if (min > 0 && max > 0) return min === max ? `${fmt(min)} Ft` : `${fmt(min)} - ${fmt(max)} Ft`;
  if (min > 0) return `${fmt(min)} Ft-tól`;
  if (max > 0) return `${fmt(max)} Ft-ig`;
  return '';
};

// A szolgáltató kártyák alapszíne (a kiemelés színe csak a keretet/ragyogást adja); a főoldali
// kategória-panelek és -chipek is ezt használják, hogy egyezzenek a kártyákkal
export const providerCardBackground = (theme) =>
  `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`;

/**
 * Szolgáltató kártya komponens
 * @param {object} provider - Szolgáltató adatai
 * @param {number} animationDelay - Animáció késleltetés (ms)
 * @param {function} onCategoryClick - Kategória chip klikk handler
 * @param {function} onCardClick - Kártya klikk handler (Quick View)
 * @param {function} onFavoriteToggle - Kedvenc váltás callback
 * @param {number} listPosition - Pozíció a listában (analitikához)
 * @param {string} listName - A lista neve az analitikához (pl. 'home_featured')
 * @param {function} categoryHref - (category) => URL; ha meg van adva, a kategória-chip valódi link (crawlolható)
 */
const ProviderCard = ({
  provider,
  animationDelay = 0,
  onCategoryClick,
  onCardClick,
  onFavoriteToggle,
  listPosition,
  listName,
  categoryHref,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Kiemelt keret/ragyogás színe szint szerint
  const getFeaturedStyle = () => {
    if (!provider.featured) return {};

    const colors = {
      TOP: {
        border: theme.palette.secondary.main,
        glow: alpha(theme.palette.secondary.main, 0.2),
        glowHover: alpha(theme.palette.secondary.main, 0.35)
      },
      TIER2: {
        border: theme.palette.primary.main,
        glow: alpha(theme.palette.primary.main, 0.2),
        glowHover: alpha(theme.palette.primary.main, 0.35)
      },
      TIER3: {
        border: theme.palette.primary.light,
        glow: alpha(theme.palette.primary.light, 0.2),
        glowHover: alpha(theme.palette.primary.light, 0.35)
      }
    };

    return colors[provider.featured.tier] || {};
  };

  const featuredStyle = getFeaturedStyle();
  const profileUrl = `/szolgaltato/${provider.slug || provider.id}`;

  const handleViewFullProfile = (e) => {
    e.stopPropagation();
    trackProviderCardClick(provider.slug || provider.id, provider.category?.name, listPosition, listName);
    // Ctrl/Cmd/középső kattintás: a böngésző nyissa meg új lapon
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    navigate(profileUrl);
  };

  const cats = provider.providerCategories?.length > 0
    ? provider.providerCategories.map((pc) => pc.category)
    : provider.category ? [provider.category] : [];
  const firstCat = cats[0];
  const extraCats = cats.slice(1);
  const firstCatHref = firstCat && categoryHref ? categoryHref(firstCat) : null;

  const handleCategoryChipClick = (e) => {
    e.stopPropagation();
    if (firstCatHref) {
      // Ctrl/Cmd/középső kattintás: a böngésző nyissa meg új lapon
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
    }
    if (onCategoryClick) onCategoryClick(firstCat.id);
    else if (firstCatHref) navigate(firstCatHref);
  };

  // A megye mező vesszővel elválasztott lista (több lefedett megye)
  const counties = (provider.county || '').split(',').map((c) => c.trim()).filter(Boolean);

  const ratingValue =parseFloat(provider.ratingAverage) || 0;
  const priceText = formatPrice(provider.priceRangeMin, provider.priceRangeMax);

  return (
    <Grow in={true} timeout={800 + animationDelay}>
      <Card
        data-provider-id={provider.id} // a főoldali megjelenés-mérés (HomeDiscoveryBands) ez alapján azonosít
        sx={{
          height: '100%',
          cursor: 'pointer',
          borderRadius: '20px',
          border: provider.featured
            ? `2px solid ${featuredStyle.border}`
            : `1px solid ${theme.palette.divider}`,
          background: providerCardBackground(theme),
          transition: 'all 0.3s ease',
          boxShadow: provider.featured
            ? `0 4px 24px ${featuredStyle.glow}`
            : '0 4px 12px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          // A hover-effektek csak valódi egérnél: érintőképernyőn ne ragadjon be
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: provider.featured
                ? `0 16px 48px ${featuredStyle.glowHover}`
                : `0 12px 40px rgba(0, 0, 0, 0.2)`,
              borderColor: provider.featured
                ? featuredStyle.border
                : theme.palette.primary.main,
              '& .image-gallery-image': {
                transform: 'scale(1.05)',
                transition: 'transform 0.3s ease-in-out',
              },
            },
          },
        }}
        onClick={() => onCardClick && onCardClick(provider)}
      >
        {/* Galéria – teljes szélességben a kártya tetején */}
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <ImageGallery
            images={provider.images}
            providerName={provider.businessName}
            providerId={provider.id}
            showThumbnails={false}
            showCaption={false}
            useThumbnails
            priority={listPosition != null && listPosition <= 3}
            imageSx={{ objectFit: 'cover' }}
            imageClassName="image-gallery-image"
            mainImageAspectRatio="75%"
          />

          {/* Kiemelt jelvény – galéria bal felső sarkában */}
          {provider.featured && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{ position: 'absolute', top: 10, left: 10, zIndex: 2, display: 'flex' }}
            >
              <FeaturedBadge featured={provider.featured} size="small" variant="overlay" />
            </Box>
          )}

          {/* Kedvenc gomb – galéria jobb felső sarkában */}
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.88)',
              borderRadius: '50%',
              minWidth: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:empty': { display: 'none' },
            }}
          >
            <FavoriteButton
              providerId={provider.id}
              size="medium"
              showTooltip={true}
              onToggle={onFavoriteToggle}
            />
          </Box>

          {/* Profilkép – a galéria alsó szélén, félig kilógva */}
          <Box
            sx={{
              position: 'absolute',
              left: 12,
              bottom: -AVATAR_OVERLAP,
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              zIndex: 3,
            }}
          >
            <Avatar
              src={provider.user?.avatarUrl || undefined}
              sx={{
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                bgcolor: 'primary.main',
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              }}
            >
              {!provider.user?.avatarUrl && (provider.user?.firstName?.charAt(0) || provider.businessName?.charAt(0))}
            </Avatar>
            {provider.status === 'APPROVED' && (
              <Tooltip title="Ellenőrzött szolgáltató" arrow>
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                }}>
                  <Verified sx={{ fontSize: 10, color: 'white' }} />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>

        <CardContent
          sx={{
            px: 2,
            pt: `${AVATAR_OVERLAP + 8}px`,
            pb: 2,
            '&:last-child': { pb: 2 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {/* Név – legfeljebb 2 sor, mindig 2 sornyi hely (egyforma kártyamagasság) */}
          <Typography
            variant="subtitle1"
            title={provider.businessName}
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.dark,
              lineHeight: 1.3,
              textAlign: 'center',
              width: '100%',
              minHeight: '2.6em',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
            }}
          >
            {provider.businessName}
          </Typography>

          {/* Értékelés – mindig látható (hover nélkül is) */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, minHeight: 22, mb: 0.75 }}>
            {ratingValue > 0 ? (
              <>
                <Rating
                  value={ratingValue}
                  precision={0.1}
                  readOnly
                  size="small"
                  sx={{ '& .MuiRating-iconFilled': { color: theme.palette.secondary.main } }}
                />
                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary, lineHeight: 1 }}>
                  {ratingValue.toFixed(1).replace('.', ',')} ({provider.reviewCount || 0})
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary, lineHeight: 1 }}>
                Még nincs értékelés
              </Typography>
            )}
          </Box>

          {/* Kategória – egy sor: az első kategória kapja a helyet, a többi „+N" */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              width: '100%',
              minWidth: 0,
              minHeight: 28,
              mb: 0.5,
            }}
          >
            {firstCat && (
              <Chip
                avatar={firstCat.icon ? <Avatar src={firstCat.icon} alt="" sx={{ width: 18, height: 18 }} /> : undefined}
                label={firstCat.name}
                title={firstCat.name}
                size="small"
                {...(firstCatHref && { component: 'a', href: firstCatHref, clickable: true })}
                onClick={handleCategoryChipClick}
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.dark,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 28,
                  minWidth: 0,
                  flex: '0 1 auto',
                  maxWidth: extraCats.length > 0 ? 'calc(100% - 44px)' : '100%',
                  cursor: 'pointer',
                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', px: 1 },
                }}
              />
            )}
            {extraCats.length > 0 && (
              <Tooltip title={extraCats.map((c) => c.name).join(', ')} arrow enterTouchDelay={0}>
                <Chip
                  label={`+${extraCats.length}`}
                  size="small"
                  sx={{ fontSize: '0.75rem', fontWeight: 600, height: 28, flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Helyszín – sima szöveg ikonnal, nem chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, minHeight: 22, width: '100%', mb: 0.75 }}>
            {counties.length > 0 && (
              <>
                <LocationOn sx={{ fontSize: 16, color: theme.palette.primary.main, flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  noWrap
                  title={counties.join(', ')}
                  sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary, minWidth: 0 }}
                >
                  {counties[0]}
                </Typography>
                {counties.length > 1 && (
                  <Tooltip title={counties.slice(1).join(', ')} arrow enterTouchDelay={0}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.8rem', color: theme.palette.primary.dark, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      +{counties.length - 1} megye
                    </Typography>
                  </Tooltip>
                )}
              </>
            )}
          </Box>

          {/* Ár – ha nincs, a helye megmarad (egyforma kártyamagasság) */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.dark,
              mb: 1.5,
              fontSize: '0.9rem',
              textAlign: 'center',
              minHeight: '1.4em',
            }}
          >
            {priceText}
          </Typography>

          {/* Gomb – mindig alul */}
          <Box sx={{ mt: 'auto', width: '100%' }}>
            <Button
              fullWidth
              component="a"
              href={profileUrl}
              variant="contained"
              size="medium"
              startIcon={<Visibility />}
              onClick={handleViewFullProfile}
              sx={{
                borderRadius: '10px',
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                py: 1,
                minHeight: 44,
                fontSize: '0.875rem',
                transition: 'all 0.2s ease-in-out',
                '@media (hover: hover)': {
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 10px ${theme.palette.primary.main}80`,
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                },
              }}
            >
              Részletek
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grow>
  );
};

export default ProviderCard;
